import { useEffect, useState } from 'react';

interface ImageWithSmartScalingProps {
  src: string;
  alt: string;
  fileName: string;
  sizeBytes: number;
}

export function ImageWithSmartScaling({
  src,
  alt,
  fileName,
  sizeBytes,
}: ImageWithSmartScalingProps) {
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Reset state when src changes
    setDimensions(null);
    setError(false);

    // Load image to get dimensions
    const img = new Image();
    img.src = src;
    img.onload = () => {
      setDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      setError(true);
    };
  }, [src]);

  if (error) {
    return (
      <div className="w-full text-center text-red-600 py-2">
        Failed to load image: {fileName}
      </div>
    );
  }

  // If we haven't loaded dimensions yet, show a placeholder with similar dimensions to avoid layout shift
  if (!dimensions) {
    return (
      <div className="w-full h-[200px] bg-gray-100 flex items-center justify-center">
        Loading...
      </div>
    );
  }

  // For small images (icons, etc), don't blow them up too much
  const isSmallImage = dimensions.width <= 200 && dimensions.height <= 200;
  const isIcon = fileName.toLowerCase().includes('icon') || sizeBytes < 10000; // Less than 10KB

  if (isSmallImage || isIcon) {
    return (
      <img
        src={src}
        alt={alt}
        className={`block h-auto mx-auto ${isIcon ? 'max-w-8' : 'max-w-16'}`}
      />
    );
  }

  // For regular images, maintain the current behavior but with smarter constraints
  return (
    <img
      src={src}
      alt={alt}
      className={`w-full h-auto max-h-[500px] object-contain mx-auto block ${
        dimensions.width > 1200 ? 'max-w-[1200px]' : 'max-w-full'
      }`}
    />
  );
}
